import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { TABLE_ROW_DRAG_DROP_KEY } from '@work4all/components/lib/dataDisplay/basic-table/components/row-render/components/row/Row';

import { useForceUpdate } from '@work4all/utils/lib/hooks/use-force-update';

import { STUECKLISTE_GROUP } from '../../hooks/use-editable-state';
import { DropZoneType, EditTableEntry } from '../../types';

import { TABLE_ROW_DND_ITEM_TYPE } from './constants';

interface UseEditTableDragProps {
  canDrop: (input: { id: string; index: number }) => number;
  index: number;
  localId: string;
  moveRowDone: (
    localId: string,
    index: number,
    dropZoneType: DropZoneType
  ) => void;
  getHovered?: () => EditTableEntry;
  getCurrent?: (id: string) => EditTableEntry;
  isMoveAllowed?: (from: EditTableEntry, to: EditTableEntry) => boolean;
  onBeforeDrag?: () => void;
  onDropItem?: (
    item?: unknown,
    position?: { index: number; dropZoneType: DropZoneType }
  ) => void;
}

type ITableRowDndItem = {
  id: string;
  index: number;
};

let dndItemRef = undefined;
let dropZoneTypeRef: DropZoneType = 'before';

export const useEditTableDrag = <
  TDrop = HTMLDivElement,
  TDrag = HTMLDivElement
>(
  props: UseEditTableDragProps
) => {
  const {
    canDrop,
    index,
    localId,
    moveRowDone,
    getHovered,
    getCurrent,
    onBeforeDrag,
    onDropItem,
  } = props;

  const dropRef = useRef<TDrop>(null);
  const dragRef = useRef<TDrag>(null);

  const forceUpdate = useForceUpdate();

  const [{ isOver, direction }, drop] = useDrop({
    accept: [TABLE_ROW_DND_ITEM_TYPE, TABLE_ROW_DRAG_DROP_KEY],
    canDrop: (item, monitor) => {
      const itemType = monitor.getItemType();
      // For external articles/items, always allow drop
      if (itemType === TABLE_ROW_DRAG_DROP_KEY) {
        return true;
      }
      // For position moves, check canDrop
      const canDropValue = canDrop(monitor.getItem());
      return Boolean(canDropValue);
    },
    collect: (monitor) => {
      const itemType = monitor.getItemType();
      // For external articles/items, always show as over
      if (itemType === TABLE_ROW_DRAG_DROP_KEY) {
        return {
          isOver: monitor.isOver(),
          direction: 'down',
        };
      }
      // For position moves, check canDrop
      const canDropValue = canDrop(monitor.getItem());

      return {
        isOver: monitor.isOver() && canDropValue,
        direction: canDropValue > 0 ? 'up' : 'down',
      };
    },
    drop: (draggedItem, monitor) => {
      const itemType = monitor.getItemType();
      // Handle external article/item drops
      if (itemType === TABLE_ROW_DRAG_DROP_KEY && onDropItem) {
        onDropItem(draggedItem, { index, dropZoneType: dropZoneTypeRef });
      }
      // Position moves are handled in the end() callback of useDrag
    },
    hover(item: ITableRowDndItem, monitor) {
      if (!dropRef.current) {
        return;
      }

      const itemType = monitor.getItemType();

      // Calculate dropZoneType based on mouse position
      let dropZoneType: DropZoneType = 'before';

      if (monitor.isOver() && dropRef.current) {
        const clientOffset = monitor.getClientOffset();
        const elementRect = (dropRef.current as any).getBoundingClientRect();

        if (clientOffset && elementRect) {
          const relativeY = clientOffset.y - elementRect.top;
          const elementHeight = elementRect.height;

          // Define drop zones: top 25%, middle 50%, bottom 25%
          if (relativeY < elementHeight * 0.25) {
            dropZoneType = 'before';
          } else if (relativeY > elementHeight * 0.75) {
            dropZoneType = 'after';
          } else {
            dropZoneType = 'inside';
          }
        }
      }

      // For external articles, just update dropZoneType and return
      if (itemType === TABLE_ROW_DRAG_DROP_KEY) {
        dropZoneTypeRef = dropZoneType;
        return;
      }

      // For position moves, continue with existing logic
      const canDropValue = canDrop(monitor.getItem());

      if (!monitor.isOver() || !dropRef.current) {
        // For non-headers, use the existing logic
        dropZoneType = canDropValue > 0 ? 'before' : 'after';
      }

      // Update the ref to trigger collect re-run
      if (dropZoneTypeRef !== dropZoneType && getHovered) {
        const hovered = getHovered();
        const current = getCurrent(item.id);

        if (
          STUECKLISTE_GROUP.includes(hovered?.positionKind) &&
          STUECKLISTE_GROUP.includes(current?.positionKind)
        ) {
          dropZoneType = 'before';
        } else if (
          hovered &&
          STUECKLISTE_GROUP.includes(hovered?.positionKind)
        ) {
          dropZoneTypeRef = dropZoneType;
          forceUpdate();
        }
      }

      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      dndItemRef = { id: item.id, index: hoverIndex };
    },
  });

  const [{ isDragging }, drag, preview] = useDrag<
    ITableRowDndItem,
    unknown,
    { isDragging: boolean }
  >({
    type: TABLE_ROW_DND_ITEM_TYPE,
    item: () => {
      onBeforeDrag?.();
      return { id: localId, index };
    },
    options: {
      dropEffect: 'move',
    },
    collect(monitor) {
      return {
        isDragging: monitor.isDragging(),
      };
    },
    end() {
      if (dndItemRef) {
        moveRowDone?.(dndItemRef.id, dndItemRef.index, dropZoneTypeRef);
        dropZoneTypeRef = 'before';
      }
    },
  });

  preview(drop(dropRef));
  drag(dragRef);

  return {
    isOver,
    direction,
    isDragging,
    dropRef,
    dragRef,
    dropZone: dropZoneTypeRef,
  };
};
